import React, { useEffect, useState } from "react";
import ComposeSentenceAnswerArea from "../components/question/ComposeSentenceAnswerArea.tsx";
import QuestionBubble from "../components/question/QuestionBubble.tsx";
import QuestionPageLayout from "../components/question/QuestionPageLayout.tsx";
import WordSelectionOption from "../components/question/WordSelectionOption.tsx";
import { useRandomCharacterImage } from "../hooks/useRandomCharacterImage.ts";

interface QuestionComposeSentencesPageProps {
  hearts: number;
  onNext: () => void;
  onBack: () => void;
  onRestart: () => void;
  progress: number;
  isLast: boolean;
  onValidate?: () => void;
  onAnswer?: (xp: number, correct: number, total: number) => void;
  onIncorrectAnswer?: () => void;
  darkMode?: boolean;
  questionConfig?: any;
  vocabularyBank?: any[];
  lessonData?: any;
}

const QuestionComposeSentencesPage = ({
  hearts,
  onNext,
  onBack,
  onRestart,
  progress,
  isLast,
  onValidate,
  onAnswer,
  onIncorrectAnswer,
  darkMode = false,
  questionConfig,
  vocabularyBank,
  lessonData,
}: QuestionComposeSentencesPageProps) => {
  // Use lesson-specific data or fallback
  const wordDict = vocabularyBank
    ? Object.fromEntries(vocabularyBank.map((w: any) => [w.id, w]))
    : {};

  console.log("🔤 ComposeSentences Debug:");
  console.log("📋 questionConfig:", questionConfig);
  console.log("📚 vocabularyBank length:", vocabularyBank?.length || 0);
  console.log("🔍 wordDict keys:", Object.keys(wordDict));

  const questionId = questionConfig?.correctAnswer?.join(",");
  const characterImg = useRandomCharacterImage([questionId]);
  const quest_batak = questionConfig?.questionSentence || [105]; // fallback
  const wordsForQuestion = quest_batak
    .map((id: number) => {
      const word = wordDict[id];
      if (!word) {
        console.warn(`⚠️ Word ID ${id} not found in vocabulary bank`);
      }
      return word;
    })
    .filter(Boolean);

  const optionIds = questionConfig?.wordBank || [];
  console.log("🎯 optionIds:", optionIds);

  const wordOptions = optionIds
    .map((id: number) => {
      const word = vocabularyBank?.find((w: any) => w.id === id);
      if (!word) {
        console.warn(`⚠️ Option word ID ${id} not found in vocabulary bank`);
        return null;
      }
      return word;
    })
    .filter(Boolean)
    .map((w: any) => w.indonesia);

  console.log("✅ wordOptions:", wordOptions);

  const correctAnswer = questionConfig?.correctAnswer || [];
  const [selectedWords, setSelectedWords] = useState<number[]>([]);
  const [draggedWord, setDraggedWord] = useState<string | null>(null);
  const [draggedFromSentenceIdx, setDraggedFromSentenceIdx] = useState<
    number | null
  >(null);
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);
  const [showPopup, setShowPopup] = useState<string | false>(false);

  // New state for checking and feedback
  const [checked, setChecked] = useState(false);
  const [isCorrect, setIsCorrect] = useState<boolean | null>(null);

  // Create stable dependency values
  const wordBankStr = questionConfig?.wordBank?.join(",");

  // Reset state when question changes
  useEffect(() => {
    console.log("🔄 ComposeSentences Question changed, resetting state");
    setSelectedWords([]);
    setDraggedWord(null);
    setDraggedFromSentenceIdx(null);
    setHoverIdx(null);
    setShowPopup(false);
    setChecked(false);
    setIsCorrect(null);
  }, [questionId, wordBankStr]);

  console.log(
    "🎮 ComposeSentences Current state - selectedWords:",
    selectedWords,
    "checked:",
    checked
  );

  const totalWords = correctAnswer.length;
  const userWords = selectedWords.length;
  const minLen = Math.min(userWords, totalWords);

  // Count correct in order (from start, up to the shorter length)
  const correctInOrder = selectedWords
    .slice(0, minLen)
    .filter((id, idx) => id === correctAnswer[idx]).length;

  // XP and accuracy: always out of the LONGER length (so extra words penalize)
  const denominator = Math.max(userWords, totalWords);
  const xp =
    denominator > 0 ? Math.round((correctInOrder / denominator) * 10) : 0;

  const handleCheck = () => {
    if (onAnswer) onAnswer(xp, correctInOrder, denominator);
    if (onValidate) onValidate();
    const correct =
      userWords === totalWords &&
      selectedWords.every((id, idx) => id === correctAnswer[idx]);
    setIsCorrect(correct);
    setChecked(true);

    // Reduce heart if answer is incorrect
    if (!correct && onIncorrectAnswer) {
      onIncorrectAnswer();
    }
  };

  // Reset for next question
  const handleContinue = () => {
    setChecked(false);
    setIsCorrect(null);
    setSelectedWords([]);
    onNext();
  };

  // Add word to sentence when clicked in word bank
  const handleWordSelect = (id: number) => {
    if (!selectedWords.includes(id)) {
      setSelectedWords([...selectedWords, id]);
    }
  };

  // Remove word from sentence area (by clicking)
  const handleRemoveSelectedWord = (idx: number) => {
    setSelectedWords(selectedWords.filter((_, i) => i !== idx));
  };

  // Drag from word bank
  const handleDragStartBank = (word: string) => {
    setDraggedWord(word);
    setDraggedFromSentenceIdx(null);
  };

  // Drag over word bank (to allow removing from sentence area)
  const handleDragOverBank = (e: React.DragEvent) => {
    e.preventDefault();
  };

  // Drop to word bank (remove from sentence)
  const handleDropBank = () => {
    if (draggedFromSentenceIdx !== null) {
      handleRemoveSelectedWord(draggedFromSentenceIdx);
    }
    setDraggedWord(null);
    setDraggedFromSentenceIdx(null);
    setHoverIdx(null);
  };

  return (
    <QuestionPageLayout
      hearts={hearts}
      onBack={onBack}
      progress={progress}
      darkMode={darkMode}
    >
      <h1
        className={`md:text-2xl text-lg font-bold text-center md:mb-8 mb-4 ${
          darkMode ? "text-gray-100" : "text-gray-800"
        }`}
        style={{ fontFamily: "Feather Bold" }}
      >
        Tuliskan dalam Bahasa Indonesia!
      </h1>

      <div className="flex items-center justify-center md:gap-6 gap-3 md:mb-10 mb-5">
        <img
          src={characterImg}
          alt="Characters"
          className="md:w-32 md:h-32 w-20 h-20 object-contain"
        />
        <QuestionBubble
          words={wordsForQuestion}
          showPopup={showPopup}
          setShowPopup={setShowPopup}
          darkMode={darkMode}
        />
      </div>

      <div className="md:space-y-4 space-y-2 md:mb-8 mb-4">
        {/* Answer area */}
        <ComposeSentenceAnswerArea
          selectedWords={selectedWords.map((id) => wordDict[id].indonesia)}
          setSelectedWords={
            checked
              ? () => {}
              : (words) => {
                  // Convert Indonesian words back to IDs if needed
                  const ids = words
                    .map((word) => {
                      const found = vocabularyBank?.find(
                        (w: any) => w.indonesia === word
                      );
                      return found ? found.id : null;
                    })
                    .filter((id) => id !== null) as number[];
                  setSelectedWords(ids);
                }
          }
          draggedWord={draggedWord}
          setDraggedWord={setDraggedWord}
          draggedFromSentenceIdx={draggedFromSentenceIdx}
          setDraggedFromSentenceIdx={setDraggedFromSentenceIdx}
          hoverIdx={hoverIdx}
          setHoverIdx={setHoverIdx}
          checked={checked}
          isCorrect={isCorrect}
          correctAnswerIds={correctAnswer}
          wordBank={vocabularyBank || []}
          darkMode={darkMode}
        />

        {/* Word bank area */}
        <WordSelectionOption
          wordOptions={wordOptions}
          selectedWords={selectedWords.map((id) => wordDict[id].indonesia)}
          draggedWord={draggedWord}
          setDraggedWord={setDraggedWord}
          setDraggedFromSentenceIdx={setDraggedFromSentenceIdx}
          setHoverIdx={setHoverIdx}
          handleWordSelect={
            checked
              ? () => {}
              : (word) => {
                  const found = vocabularyBank?.find(
                    (w: any) => w.indonesia === word
                  );
                  if (found) handleWordSelect(found.id);
                }
          }
          handleDragStartBank={checked ? () => {} : handleDragStartBank}
          handleDragOverBank={handleDragOverBank}
          handleDropBank={checked ? () => {} : handleDropBank}
          checked={checked}
          correctAnswerIndo={correctAnswer.map((id) => wordDict[id].indonesia)}
          darkMode={darkMode}
        />
      </div>

      <div className="flex justify-center md:mt-4 mt-2">
        {!checked ? (
          <button
            onClick={handleCheck}
            disabled={selectedWords.length === 0}
            className={`md:px-8 px-5 md:py-3 py-2 rounded-lg font-bold md:text-base text-sm transition-colors
              ${
                selectedWords.length === 0
                  ? darkMode
                    ? "bg-gray-700 text-gray-400 cursor-not-allowed"
                    : "bg-gray-400 text-white cursor-not-allowed"
                  : darkMode
                  ? "bg-blue-700 hover:bg-blue-800 text-white"
                  : "bg-blue-500 hover:bg-blue-600 text-white"
              }
            `}
            style={{ fontFamily: "DIN Next Rounded" }}
          >
            Periksa
          </button>
        ) : (
          <button
            onClick={handleContinue}
            className={`md:px-8 px-5 md:py-3 py-2 rounded-lg font-bold md:text-base text-sm transition-colors text-white ${
              darkMode
                ? "bg-lime-700 hover:bg-lime-800"
                : "bg-lime-500 hover:bg-lime-600"
            }`}
            style={{ fontFamily: "DIN Next Rounded" }}
          >
            {isLast ? "Selesai" : "Lanjutkan"}
          </button>
        )}
      </div>
    </QuestionPageLayout>
  );
};

export default QuestionComposeSentencesPage;
